import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import SelectField from 'components/SelectField';
import TrashIcon from 'icons/Trash.icon';
import {
  SortByForm,
  SORTING_ORDER,
  SortingDirectionValues,
  SortingField,
} from 'models/Sort';
import { Button } from 'ncoded-component-library';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import PlusIcon from 'icons/Plus.icon';

import './MultiSortOptions.styles.scss';

export const INITIAL_VALUE = {
  name: '',
  order: SORTING_ORDER.A_TO_Z,
} as SortingField;

export type MultiSortOptionsProps = {
  className?: string;
  options: OptionValue[];
  sortDirectionTranslations?: SortingDirectionValues;
};

const MultiSortOptions: React.FC<MultiSortOptionsProps> = (props) => {
  const { className, options, sortDirectionTranslations } = props;

  const classes = classNames(
    'multi-sort-options',
    'multi-fields-options',
    className,
  );

  const { t } = useTranslation();

  const {
    values: { sortBy },
  } = useFormState<SortByForm>();

  return (
    <div className={classes}>
      <FieldArray name="sortBy">
        {({ fields }) => (
          <>
            <div>
              {fields.map((name, index) => {
                const isFirstElement = index === 0;

                const { name: sortName } = fields.value[index] || {};

                const sortDirectionTranslation =
                  sortDirectionTranslations?.[sortName];

                return (
                  <div key={name} className="row">
                    <div>
                      <p>{t(isFirstElement ? 'sortBy' : 'thenBy')}</p>

                      <Field
                        name={`${name}.name`}
                        component={SelectField}
                        renderAsPortal
                        portalClassName="multi-sort-select"
                        options={options.map((el) => ({
                          ...el,
                          disabled: sortBy.find(
                            ({ name }) => name === el.value,
                          ),
                        }))}
                      />
                    </div>

                    <div>
                      {sortName && (
                        <>
                          <Field
                            name={`${name}.order`}
                            type="radio"
                            value={SORTING_ORDER.A_TO_Z as string}
                            component={RadioButtonField}
                            label={t(
                              sortDirectionTranslation?.a_to_z || 'aToZ',
                            )}
                          />
                          <Field
                            name={`${name}.order`}
                            type="radio"
                            value={SORTING_ORDER.Z_TO_A as string}
                            component={RadioButtonField}
                            label={t(
                              sortDirectionTranslation?.z_to_a || 'zToA',
                            )}
                          />
                        </>
                      )}

                      {!isFirstElement ? (
                        <Button
                          variant="icon"
                          className="warning-action-button"
                          icon={TrashIcon}
                          onClick={() => fields.remove(index)}
                        />
                      ) : (
                        <div className="button-placeholder" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {fields.length < options.length && (
              <Button
                icon={PlusIcon}
                variant="outline"
                onClick={() => fields.push(INITIAL_VALUE)}
              >
                {t('addSort')}
              </Button>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default MultiSortOptions;
